import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { storage } from "@/helper/storage";
import { useLoadAuth } from "@/services/useLoadAuth";

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginResponse {
  access_token: string;
  id: string;
  role: string;
  username: string;
  user: {
    id: string;
    email: string;
    name?: string;
  };
}

interface User {
  id: string;
  email: string;
  name?: string;
}

type Auth = {
  id: string;
  role: string;
  sub: string;
  username: string;
};

type AuthContextType = {
  isAuthenticated: boolean;
  token: string | null;
  loading: boolean;
  login: (
    email: string,
    password: string,
    redirectTo?: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  user: Auth | null;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  token: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  user: null,
});

export const useLogin = () => {
  return useMutation({
    mutationFn: async (
      credentials: LoginCredentials
    ): Promise<LoginResponse> => {
      const response = await api.post("/auth/login", {
        username: credentials.email,
        password: credentials.password,
      });
      return response.data;
    },
    onSuccess: async (data) => {
      await storage.setItem("token", data.access_token);
      await storage.setItem(
        "user",
        JSON.stringify({
          id: data.id,
          role: data.role,
          username: data.username,
        })
      );
    },
    onError: (error) => {
      console.error("Login error:", error);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const fcmToken = await storage.getItem("fcm_token");
      try {
        await api.post("/auth/logout", {
          token: fcmToken,
        });
        await storage.removeItem("fcm_token");
      } catch (error) {
        console.warn("Server logout failed:", error);
      }
    },
    onSuccess: async () => {
      await storage.removeItem("token");
      await storage.removeItem("user");
      queryClient.clear();
      window.location.replace("/login");
    },
  });
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { token, user, loading, setToken, setUser } = useLoadAuth();
  const { mutateAsync: loginMutation } = useLogin();
  const { mutateAsync: logoutMutation } = useLogout();

  if (loading) {
    // Mostrar pantalla de carga mientras se lee Capacitor Preferences
    return (
      <div style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        Cargando...
      </div>
    );
  }

  const login = async (
    email: string,
    password: string,
    redirectTo?: string
  ) => {
    await loginMutation(
      { email, password },
      {
        onSuccess: async () => {
          const savedToken = await storage.getItem("token");
          const savedUser = await storage.getItem("user");

          if (savedToken) setToken(savedToken);
          if (savedUser) setUser(JSON.parse(savedUser));

          if (redirectTo) window.location.replace(redirectTo);
        },
      }
    );
  };

  const logout = async () => logoutMutation();

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!token,
        token,
        loading,
        login,
        logout,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
